// AI Logic for Tic Tac Toe - Minimax Algorithm

export const checkWinner = (board, size, winLength) => {
  const directions = [
    [0, 1],   // horizontal
    [1, 0],   // vertical
    [1, 1],   // diagonal down-right
    [1, -1],  // diagonal down-left
  ];

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const cell = board[row][col];
      if (!cell) continue;

      for (const [dx, dy] of directions) {
        let count = 1;
        let winningCells = [[row, col]];

        // Check forward
        for (let i = 1; i < winLength; i++) {
          const newRow = row + dx * i;
          const newCol = col + dy * i;
          if (
            newRow >= 0 && newRow < size &&
            newCol >= 0 && newCol < size &&
            board[newRow][newCol] === cell
          ) {
            count++;
            winningCells.push([newRow, newCol]);
          } else {
            break;
          }
        }

        if (count >= winLength) {
          return { winner: cell, winningCells };
        }
      }
    }
  }

  // Check for draw
  const isDraw = board.every(row => row.every(cell => cell !== null));
  if (isDraw) {
    return { winner: 'draw', winningCells: [] };
  }

  return null;
};

export const getEmptyCells = (board, size) => {
  const emptyCells = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (!board[row][col]) {
        emptyCells.push([row, col]);
      }
    }
  }
  return emptyCells;
};

// Evaluate board score for minimax
const evaluateBoard = (board, size, winLength, player) => {
  const result = checkWinner(board, size, winLength);
  if (result) {
    if (result.winner === player) return 100;
    if (result.winner === 'draw') return 0;
    return -100;
  }
  return 0;
};

// Heuristic evaluation for larger boards
const heuristicEvaluate = (board, size, winLength, player) => {
  const opponent = player === 'X' ? 'O' : 'X';
  let score = 0;

  const directions = [
    [0, 1], [1, 0], [1, 1], [1, -1],
  ];

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col] === player) {
        for (const [dx, dy] of directions) {
          let count = 1;
          let openEnds = 0;

          // Count forward
          for (let i = 1; i < winLength; i++) {
            const newRow = row + dx * i;
            const newCol = col + dy * i;
            if (newRow >= 0 && newRow < size && newCol >= 0 && newCol < size) {
              if (board[newRow][newCol] === player) count++;
              else if (board[newRow][newCol] === null) { openEnds++; break; }
              else break;
            }
          }

          // Score based on line length
          if (count >= winLength) score += 1000;
          else if (count === winLength - 1 && openEnds > 0) score += 50;
          else if (count === winLength - 2 && openEnds > 0) score += 10;
        }
      } else if (board[row][col] === opponent) {
        // Penalize opponent's progress
        for (const [dx, dy] of directions) {
          let count = 1;
          for (let i = 1; i < winLength; i++) {
            const newRow = row + dx * i;
            const newCol = col + dy * i;
            if (newRow >= 0 && newRow < size && newCol >= 0 && newCol < size) {
              if (board[newRow][newCol] === opponent) count++;
              else break;
            }
          }
          if (count >= winLength - 1) score -= 100;
        }
      }
    }
  }

  return score;
};

// Minimax with alpha-beta pruning
const minimax = (board, size, winLength, depth, isMaximizing, player, alpha, beta) => {
  const result = checkWinner(board, size, winLength);
  
  if (result) {
    if (result.winner === player) return 100 - depth;
    if (result.winner === 'draw') return 0;
    return depth - 100;
  }

  if (depth === 0) {
    return heuristicEvaluate(board, size, winLength, player);
  }

  const emptyCells = getEmptyCells(board, size);
  if (emptyCells.length === 0) return 0;

  // For large boards, limit search area to cells near existing pieces
  let cellsToCheck = emptyCells;
  if (size > 5 && emptyCells.length > 20) {
    cellsToCheck = getRelevantCells(board, size, emptyCells);
  }

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const [row, col] of cellsToCheck) {
      board[row][col] = player;
      const evalScore = minimax(board, size, winLength, depth - 1, false, player, alpha, beta);
      board[row][col] = null;
      maxEval = Math.max(maxEval, evalScore);
      alpha = Math.max(alpha, evalScore);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    const opponent = player === 'X' ? 'O' : 'X';
    let minEval = Infinity;
    for (const [row, col] of cellsToCheck) {
      board[row][col] = opponent;
      const evalScore = minimax(board, size, winLength, depth - 1, true, player, alpha, beta);
      board[row][col] = null;
      minEval = Math.min(minEval, evalScore);
      beta = Math.min(beta, evalScore);
      if (beta <= alpha) break;
    }
    return minEval;
  }
};

// Get cells near existing pieces for more efficient search
const getRelevantCells = (board, size, emptyCells) => {
  const relevant = new Set();
  const range = 2;

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col]) {
        for (let dr = -range; dr <= range; dr++) {
          for (let dc = -range; dc <= range; dc++) {
            const newRow = row + dr;
            const newCol = col + dc;
            if (
              newRow >= 0 && newRow < size &&
              newCol >= 0 && newCol < size &&
              !board[newRow][newCol]
            ) {
              relevant.add(`${newRow},${newCol}`);
            }
          }
        }
      }
    }
  }

  if (relevant.size === 0) {
    // First move - center or near center
    const center = Math.floor(size / 2);
    return [[center, center]];
  }

  return Array.from(relevant).map(key => {
    const [row, col] = key.split(',').map(Number);
    return [row, col];
  });
};

// Get AI move based on difficulty
export const getAIMove = (board, size, winLength, difficulty, aiPlayer = 'O') => {
  const emptyCells = getEmptyCells(board, size);
  if (emptyCells.length === 0) return null;

  // Difficulty settings
  const depthMap = {
    easy: 0,
    medium: 2,
    hard: 4,
    expert: 6,
  };

  const depth = depthMap[difficulty] || 2;

  // Easy: Random move
  if (difficulty === 'easy') {
    const randomIndex = Math.floor(Math.random() * emptyCells.length);
    return emptyCells[randomIndex];
  }

  // Check for immediate win
  for (const [row, col] of emptyCells) {
    board[row][col] = aiPlayer;
    if (checkWinner(board, size, winLength)?.winner === aiPlayer) {
      board[row][col] = null;
      return [row, col];
    }
    board[row][col] = null;
  }

  // Check for immediate block
  const humanPlayer = aiPlayer === 'X' ? 'O' : 'X';
  for (const [row, col] of emptyCells) {
    board[row][col] = humanPlayer;
    if (checkWinner(board, size, winLength)?.winner === humanPlayer) {
      board[row][col] = null;
      return [row, col];
    }
    board[row][col] = null;
  }

  // For Gomoku (larger boards), use optimized search
  let cellsToCheck = emptyCells;
  if (size > 5) {
    cellsToCheck = getRelevantCells(board, size, emptyCells);
    if (cellsToCheck.length === 0) {
      cellsToCheck = emptyCells;
    }
  }

  // Minimax for best move
  let bestMove = null;
  let bestScore = -Infinity;

  for (const [row, col] of cellsToCheck) {
    board[row][col] = aiPlayer;
    const score = minimax(board, size, winLength, depth, false, aiPlayer, -Infinity, Infinity);
    board[row][col] = null;

    if (score > bestScore) {
      bestScore = score;
      bestMove = [row, col];
    }
  }

  return bestMove || emptyCells[0];
};

// Create empty board
export const createEmptyBoard = (size) => {
  return Array(size).fill(null).map(() => Array(size).fill(null));
};
